// core
import { Injectable, Inject, Renderer2, RendererFactory2 } from '@angular/core';
import { DOCUMENT } from '@angular/common';
import { Observable, Subject } from 'rxjs';

// campfire
import { PartnerBrandService } from '@campfire-nx/api';

// Raptor
import { RaptorQuestionService } from './raptor-question.service';
import { RaptorStepDataService } from './raptor-step-data.service';
import { RaptorStripeService } from '../resources/raptor-stripe.service';
import { RaptorGocardlessService } from '../resources/raptor-gocardless.service';

declare global {
  interface Window { Stripe: any }
}

@Injectable({
  providedIn: 'root'
})
export class RaptorPaymentService {
  private renderer: Renderer2;
  paymentType?: any = null;
  displayError = false;
  productSlug?: string;
  account?: string;
  paying: boolean = false;


  // stripe vars
  stripeService: any;
  stripeErrors: any = {};
  stripeFields: any = {};
  // 50 is default minimum value for AUD to set payment Element. Not used by BE to set payment intent
  // Documentation: https://docs.stripe.com/currencies#minimum-and-maximum-charge-amounts
  payableFirstInCents: number = 50;

  // Observable/Subject for when enough payment details have changed that other
  // components need to react
  paymentDetails$: Observable<boolean>;
  private paymentDetailsSubject: Subject<boolean>;

  // Gocardless vars
  gocardlessAccount: any = {};
  gocardlessShowAccount: boolean = false;
  gocardlessErrors: any = {};
  paymentElement: any;

  option!: any;
  submitLabel!: string | null;

  constructor(
    rendererFactory: RendererFactory2,
    @Inject(DOCUMENT) private document: Document,
    @Inject('environment') public environment: any,
    private raptorStepDataService: RaptorStepDataService,
    private raptorQuestionService: RaptorQuestionService,
    private raptorStripeService: RaptorStripeService,
    private raptorGocardlessService: RaptorGocardlessService,
    private partnerBrandService: PartnerBrandService
  ) {
    // create a new renderer
    // https://stackoverflow.com/questions/34710408/renderer-cannot-be-used-in-service#47924814
    this.renderer = rendererFactory.createRenderer(null, null);

    this.paymentDetailsSubject = new Subject<any>();
    this.paymentDetails$ = this.paymentDetailsSubject.asObservable();
  }

  public startStep() {
    let stepData = this.raptorStepDataService.get();
    if (stepData && stepData.data) {
      this.productSlug = stepData.data.product_slug;

      // Getting amount in cents to use while initiating Stripe PaymentElement
      if (
        stepData.covers &&
        stepData.covers.length > 0 &&
        stepData.covers[0].premium[stepData.data.frequency] &&
        stepData.covers[0].premium[stepData.data.frequency].payable_first_invoice
      ) {
        this.payableFirstInCents = Math.round(
          stepData.covers[0].premium[stepData.data.frequency].payable_first_invoice * 100
        );
      }
    }
    this.paymentType = null;
    this.submitLabel = null;
  }

  public calculatePayableFirstInCents(field:any, formValues:any) {
    if (field === 'frequency') {
      let fieldValue = formValues[field];
      let stepData = this.raptorStepDataService.get();
      
      if (
        stepData &&
        stepData.data &&
        stepData.covers &&
        stepData.covers.length > 0 &&
        stepData.covers[0].premium[fieldValue] &&
        stepData.covers[0].premium[fieldValue].payable_first_invoice
      ) {
        this.payableFirstInCents = Math.round(
          stepData.covers[0].premium[fieldValue].payable_first_invoice * 100
        );
        this.paymentDetailsSubject.next(true);
      }
    }
  }

  public hasError(): boolean {
    let formError = false;
    if (this.paymentType === 'stripe') {
      for (let key in this.stripeErrors) {
        if (this.stripeErrors[key]) {
          formError = true;
        }
      }
    }
    return formError;
  }

  public pay(form: any): Promise<any> {
    var paymentPromise;

    this.paying = true;
    if (this.paymentType === 'stripe') {
      paymentPromise = this.stripePayment(form);
    } else if (this.paymentType === 'gocardless') {
      paymentPromise = this.gocardlessPayment(form);
    }

    return paymentPromise ? paymentPromise : Promise.resolve();
  }

  public loadStripe(option: any, callback: Function): void {
    let stripeSdk = this.document.getElementById('stripeSDK');

    this.paymentType = 'stripe';
    this.account = option.account;
    this.displayError = false;
    this.paying = false;
    this.stripeErrors = {};
    this.submitLabel = option.submit_label;

    if (!stripeSdk) {
      let stripeSdk = this.renderer.createElement('script');
      stripeSdk.src = 'https://js.stripe.com/v3/';
      stripeSdk.id = 'stripeSDK';
      stripeSdk.onload = () => {
        this.stripeService = window.Stripe(option.params.api_key, {
          betas : ['blocked_card_brands_beta_2']
        });
        callback();
      }
      this.renderer.appendChild(this.document.body, stripeSdk);
    } else {
      this.stripeService = window.Stripe(option.params.api_key, {
        betas : ['blocked_card_brands_beta_2']
      });
      callback();
    }
  }

  public stripePayment(form: any): Promise<any> {
    let paymentField = this.raptorQuestionService.getQuestionByAttribute(this.raptorStepDataService.get(), 'type', 'payment');
    let settings = paymentField.settings.payment_options.find((option: any) => option.service === 'stripe');

    return this.paymentElement.submit()
      .then(() => {
        // country value is mandatory to createPaymentMethod with payment Element
        // TODO: Needs to be dynamic based on policy/product
        return this.stripeService.createPaymentMethod({
          elements: this.paymentElement,
          params: {
            type: 'payment',
            billing_details: {
              email: settings.params.user_email,
              address: {
                country: this.partnerBrandService.data.product?.country?.country_alpha2 ||
                  paymentField?.settings?.locale?.country_alpha2
              }
            }
          }
        })
          .then((data: any) => {
            if (data.error) {
              this.stripeErrors.cardFailure = data.error.message;
              return;
            } else {
              this.stripeErrors = {};
              return this.raptorStripeService.save({
                payment_method_id: data.paymentMethod.id,
                user_slug: settings.params.user_slug,
                product_slug: this.productSlug
              })
                .then((response: any) => {
                  return response;
                })
                .catch((response: any) => {
                  this.stripeErrors.cardFailure = response.error && response.error.error;
                  return response;
                });
            }
          });
      });
  }

  public loadGocardless(option: any) {
    this.paymentType = 'gocardless';
    this.account = option.account;
    this.displayError = false;
    this.paying = false;
    this.gocardlessShowAccount = false;
    this.option = option;
    // Submit label is initially the pre-submit label for gocardless
    // This changes after confirming direct debit details
    this.submitLabel = option.pre_submit_label;
  }

  public gocardlessCreate(form: any) {
    let paymentField = this.raptorQuestionService.getQuestionByAttribute(this.raptorStepDataService.get(), 'type', 'payment');
    let settings = paymentField.settings.payment_options.find((option: any) => option.service === 'gocardless');
    let ddname = form.get(paymentField.key + '.' + settings.service + '.ddname').value;
    let ddbsb = form.get(paymentField.key + '.' + settings.service + '.ddbsb').value;
    let ddaccnum = form.get(paymentField.key + '.' + settings.service + '.ddaccnum').value;
    let payload = {
      account_name: ddname,
      bsb: ddbsb,
      account_number: ddaccnum,
      user_slug: settings.params.user_slug,
      product_slug: this.productSlug
    };

    this.gocardlessErrors = {};

    return this.raptorGocardlessService.createAccount(payload)
      .then((result: any) => {
        if (result.error) {
          this.displayError = true;
          this.gocardlessErrors.cardFailure = result.error;
        } else {
          this.gocardlessAccount = result;
          this.gocardlessShowAccount = true;
          this.submitLabel = this.option.submit_label;
        }
      })
      .catch((result: any) => {
        this.displayError = true;
        if (result.error && result.error.error) {
          this.gocardlessErrors.cardFailure = result.error.error;
        }
      });
  }

  public gocardlessPayment(form: any) {
    let paymentField = this.raptorQuestionService.getQuestionByAttribute(this.raptorStepDataService.get(), 'type', 'payment');
    let settings = paymentField.settings.payment_options.find((option: any) => option.service === 'gocardless');

    let payload = {
      billing_request_id: this.gocardlessAccount.billing_request_id,
      user_slug: settings.params.user_slug
    }

    this.gocardlessErrors = {};

    return this.raptorGocardlessService.confirmPayment(payload)
      .then((response: any) => {
        return response;
      })
      .catch((response: any) => {
        return response;
      });
  }
}
